/**
 * Gradients System
 * Defines background gradients for mobile, compact, and web viewports
 * and predefined gradient configurations for borders and backgrounds
 */

import { Viewport } from './breakpoints';
import { ColorType, getColorValue } from './colors';

export enum GradientType {
  Background = 'background',
}

export const gradients = {
  [GradientType.Background]: {
    [Viewport.Mobile]: 'linear-gradient(195deg, rgb(0 43 31) 0%, rgb(0 12 30) 100%)',
    [Viewport.Compact]: 'linear-gradient(194deg, #00371D 0%, #000E2A 100%)',
    [Viewport.Web]: 'linear-gradient(194deg, #00371D 0%, #000E2A 100%)',
  },
} as const;

/**
 * Get gradient value for specific type and viewport
 */
export function getGradient(type: GradientType, viewport: Viewport): string {
  return gradients[type][viewport];
}

/**
 * Get background gradient for viewport
 */
export function getBackgroundGradient(viewport: Viewport): string {
  return getGradient(GradientType.Background, viewport);
}

/**
 * Gradient color stop
 * Supports both ColorType enum values and raw hex strings for custom colors
 */
export interface GradientColorStop {
  color: ColorType | string;
  opacity?: number;
  stop: number; // 0-100
}

/**
 * Gradient configuration
 * Can be used for both borders and backgrounds
 */
export interface BorderGradient {
  angle: number; // degrees
  colors: GradientColorStop[];
}

/**
 * Build CSS linear-gradient string from BorderGradient config
 */
export function buildGradientString(gradient: BorderGradient): string {
  const colorStops = gradient.colors
    .map(({ color, opacity, stop }) => {
      const colorValue = getColorValue(color, opacity);
      return `${colorValue} ${stop}%`;
    })
    .join(', ');

  return `linear-gradient(${gradient.angle}deg, ${colorStops})`;
}

// ============================================================================
// PREDEFINED GRADIENTS
// Easy-to-use gradient presets for common use cases
// ============================================================================

/**
 * Predefined gradient names
 * Use these to reference common gradient patterns throughout the app
 */
export enum GradientPreset {
  // Border Gradients
  TileBorder = 'tileBorder',
  SubtleFade = 'subtleFade',
  AccentGlow = 'accentGlow',
  AccentToWhite = 'accentToWhite',
  WhiteAccentWhite = 'whiteAccentWhite',

  // Background Gradients
  DarkPanel = 'darkPanel',
  GlassyCard = 'glassyCard',
}

/**
 * Predefined gradient configurations
 * Reference these by name using GradientPreset enum
 */
export const gradientPresets: Record<GradientPreset, BorderGradient> = {
  // Border Gradients
  [GradientPreset.TileBorder]: {
    angle: 180,
    colors: [
      { color: ColorType.Accent, opacity: 0.08, stop: 0 },
      { color: ColorType.Accent, opacity: 0.06, stop: 28 },
      { color: ColorType.White, opacity: 0, stop: 99 },
    ],
  },
  [GradientPreset.SubtleFade]: {
    angle: 90,
    colors: [
      { color: ColorType.Accent, opacity: 1, stop: 0 },
      { color: ColorType.Accent, opacity: 0.2, stop: 100 },
    ],
  },
  [GradientPreset.AccentGlow]: {
    angle: 135,
    colors: [
      { color: ColorType.Accent, opacity: 0.8, stop: 0 },
      { color: ColorType.White, opacity: 0.2, stop: 100 },
    ],
  },
  [GradientPreset.AccentToWhite]: {
    angle: 135,
    colors: [
      { color: ColorType.Accent, opacity: 0.8, stop: 0 },
      { color: ColorType.White, opacity: 0.4, stop: 50 },
      { color: ColorType.Accent, opacity: 0.8, stop: 100 },
    ],
  },
  [GradientPreset.WhiteAccentWhite]: {
    angle: 45,
    colors: [
      { color: ColorType.White, opacity: 0.6, stop: 0 },
      { color: ColorType.Accent, opacity: 0.6, stop: 50 },
      { color: ColorType.White, opacity: 0.6, stop: 100 },
    ],
  },
  
  // Background Gradients
  [GradientPreset.DarkPanel]: {
    angle: 180,
    colors: [
      { color: ColorType.Black, opacity: 0.6, stop: 0 },
      { color: ColorType.Black, opacity: 0.8, stop: 100 },
    ],
  },
  [GradientPreset.GlassyCard]: {
    angle: 145,
    colors: [
      { color: ColorType.White, opacity: 0.11, stop: 0 },
      { color: ColorType.White, opacity: 0.01, stop: 100 },
    ],
  },
};

/**
 * Get a predefined gradient configuration by name
 * @param preset - GradientPreset enum value
 * @returns BorderGradient configuration
 */
export function getGradientPreset(preset: GradientPreset): BorderGradient {
  return gradientPresets[preset];
}

/**
 * Get a predefined gradient as a CSS string
 * @param preset - GradientPreset enum value
 * @returns CSS linear-gradient string
 */
export function getGradientPresetString(preset: GradientPreset): string {
  return buildGradientString(gradientPresets[preset]);
}
